import React, { useState, useCallback } from 'react';
import { ArrowLeft, Warehouse, Clock, ClipboardList, FileBarChart2 } from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';
import LagerSidebar from './lager/LagerSidebar';
import LagerDashboard from './LagerDashboard';
import LagerView from './LagerView';
import BanfStatusView from './lager/BanfStatusView';
import DurchlaufzeitenView from './lager/DurchlaufzeitenView';
import BerichteView from './lager/BerichteView';
import lagerNavState from './lager/lagerNavState';

/**
 * LagerShell — Rahmen des Lager-Moduls mit Sidebar-Navigation.
 * Startseite ist das Dashboard; Sidebar steuert activeSection.
 * Props:
 *   user                   Object  (aus useAuth)
 *   onBack                 () => void   zurueck zur Startseite
 *   showSuccess            (msg) => void
 *   showError              (msg) => void
 *   onCartUpdated          () => void
 *   onNavigateToCreateBanf (prefill) => void
 */
const LagerShell = ({
  user,
  onBack,
  showSuccess,
  showError,
  onCartUpdated,
  onNavigateToCreateBanf,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // Modul-Navigation — Position wird über Unmounts (z. B. Warenkorb-Round-Trip)
  // in lagerNavState gehalten, damit "Zurück" an dieselbe Stelle führt.
  const [activeSection, setActiveSectionState] = useState(lagerNavState.activeSection);
  const setActiveSection = useCallback((s) => {
    lagerNavState.activeSection = s;
    setActiveSectionState(s);
  }, []);

  // Gemeinsames Werk (zwischen Dashboard und Lagerbestand geteilt)
  const [selectedWerk, setSelectedWerk] = useState(lagerNavState.selectedWerk);

  // Drill-down-Filter: wird gesetzt, wenn KPI-Karte angeklickt
  // und nach Anwendung in LagerView wieder auf null gesetzt
  const [pendingFilter, setPendingFilter] = useState(null);

  // Drill-down: Filter setzen und zu Lagerbestand wechseln
  const handleDrilldown = useCallback((filter) => {
    setPendingFilter(filter);
    setActiveSection('lagerbestand');
  }, []);

  // Werk-Aenderung: einheitlich fuer Shell (+ Position persistieren)
  const handleWerkChange = useCallback((werk) => {
    lagerNavState.selectedWerk = werk;
    setSelectedWerk(werk);
  }, []);

  // Nach Anwendung des pendingFilter durch LagerView zuruecksetzen
  const handleFilterApplied = useCallback(() => {
    setPendingFilter(null);
  }, []);

  const bgCls = isDarkMode
    ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800'
    : 'bg-slate-50';

  const headerBg = isDarkMode
    ? 'bg-gray-900/20 border-b border-white/10 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
    : 'bg-white border-b border-slate-200';

  const iconBoxCls = isDarkMode
    ? 'bg-gradient-to-br from-slate-600/60 via-gray-600/50 to-slate-700/60 border border-white/20 shadow-[0_6px_24px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.3)]'
    : 'bg-brand/10';

  const titleCls = isDarkMode
    ? 'bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent text-xl font-semibold'
    : 'text-slate-900 text-xl font-semibold';

  const subtitleCls = isDarkMode
    ? 'text-white/50 text-xs mt-0.5'
    : 'text-slate-500 text-xs mt-0.5';

  const backBtnCls = isDarkMode
    ? 'p-2 rounded-xl transition-colors hover:bg-white/10 text-white/70 hover:text-white flex-shrink-0'
    : 'p-2 rounded-xl transition-colors hover:bg-slate-100 text-slate-500 hover:text-slate-700 flex-shrink-0';

  // Platzhalter fuer deaktivierte Sektionen
  const renderPlaceholder = (labelKey, Icon) => (
    <div className={`flex-1 flex flex-col items-center justify-center gap-4 ${isDarkMode ? 'text-white/30' : 'text-slate-300'}`}>
      <Icon className="w-12 h-12" />
      <div className="text-sm font-medium">{t(labelKey)}</div>
      <div className={`text-xs ${isDarkMode ? 'text-white/20' : 'text-slate-400'}`}>{t('lm.dashSoon')}</div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <LagerDashboard
            user={user}
            selectedWerk={selectedWerk}
            onWerkChange={handleWerkChange}
            onDrilldown={handleDrilldown}
          />
        );
      case 'lagerbestand':
        return (
          <LagerView
            user={user}
            showSuccess={showSuccess}
            showError={showError}
            onCartUpdated={onCartUpdated}
            onNavigateToCreateBanf={onNavigateToCreateBanf}
            initialWerk={selectedWerk}
            onWerkChange={handleWerkChange}
            initialFilter={pendingFilter}
            onFilterApplied={handleFilterApplied}
          />
        );
      case 'banf-status':
        return <BanfStatusView user={user} />;
      case 'durchlaufzeiten':
        return <DurchlaufzeitenView user={user} selectedWerk={selectedWerk} onWerkChange={handleWerkChange} />;
      case 'berichte':
        return <BerichteView user={user} selectedWerk={selectedWerk} onWerkChange={handleWerkChange} />;
      default:
        return null;
    }
  };

  return (
    <div className={`flex-1 flex flex-col min-h-0 ${bgCls}`}>

      {/* ---- Modul-Header (schmal, nur Zurueck + Titel) ---- */}
      <div className="relative flex-shrink-0">
        <div className={`absolute inset-0 ${headerBg}`} />
        <div className="relative max-w-full px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-3">
            {typeof onBack === 'function' && (
              <button
                type="button"
                onClick={onBack}
                className={backBtnCls}
                aria-label={t('lm.backToHome')}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBoxCls}`}>
                <Warehouse className={`w-4 h-4 ${isDarkMode ? 'text-white/90' : 'text-brand'}`} />
              </div>
              <div>
                <h1 className={titleCls}>{t('lm.title')}</h1>
                <p className={subtitleCls}>{t('lm.subtitle')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Body: Sidebar + Inhalt ---- */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <LagerSidebar
          activeSection={activeSection}
          onSelect={setActiveSection}
        />
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default LagerShell;
